import React, { useEffect, useState } from 'react';
import { Callout, EmptyContent, Flex, Text } from '@/components/ui';
import { DataTable } from '@/components/data-table';
import { CSVPreviewData, CSVPreviewProps, PreviewState } from '../types';
import { parseCSVContent, validateCSVContent } from '../utils/csvParser';
import {
  IconAlertCircle,
  IconAlertTriangleFilled,
  IconFileText,
  IconTable,
} from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { api } from '@/services/http';

const CSVPreviewRenderer: React.FC<CSVPreviewProps> = ({
  apiEndpoint,
  fileUrl,
  maxPreviewRows = 10000,
}) => {
  const [state, setState] = useState<PreviewState>({
    loading: true,
    error: null,
    data: null,
  });

  useEffect(() => {
    let isCancelled = false;

    const loadCSVContent = async () => {
      setState({ loading: true, error: null, data: null });

      try {
        let csvText: string;

        // If fileUrl is provided (blob URL for uploaded files), use fetch
        if (fileUrl) {
          const response = await fetch(fileUrl, {
            method: 'GET',
            credentials: 'omit', // No credentials for blob URLs
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          csvText = await response.text();
        } else if (apiEndpoint) {
          // Use axios api for apiEndpoint to respect baseURL and authentication
          const response = await api.get(apiEndpoint, {
            responseType: 'text',
            headers: {
              Accept: 'text/csv, text/plain, text/*, */*',
            },
            timeout: 30000, // 30 second timeout
          });

          csvText = response.data;
        } else {
          throw new Error(
            'API endpoint or file URL is required for file download'
          );
        }

        if (isCancelled) return;

        const validation = validateCSVContent(csvText);
        if (!validation.valid) {
          setState({
            loading: false,
            error: 'parse-error',
            data: { message: validation.error },
          });
          return;
        }

        const csvData = parseCSVContent(csvText, maxPreviewRows);

        if (isCancelled) return;

        setState({
          loading: false,
          error: null,
          data: csvData,
        });
      } catch (error: unknown) {
        if (isCancelled) return;

        const errorLike =
          typeof error === 'object' && error !== null
            ? (error as { code?: string; response?: { status?: number } })
            : null;

        let errorType:
          | 'download-failed'
          | 'network-error'
          | 'parse-error'
          | 'access-denied' = 'network-error';
        let errorMessage = 'Unknown error';
        let errorDetails: string | undefined;

        if (error instanceof Error) {
          errorMessage = error.message;

          // Check for specific error types
          if (error.message.includes('ACCESS_DENIED')) {
            errorType = 'access-denied';
            errorDetails = "You don't have permission to preview this file.";
          } else if (
            error.name === 'AbortError' ||
            error.message.includes('timeout') ||
            errorLike?.code === 'ECONNABORTED'
          ) {
            errorType = 'network-error';
            errorDetails = 'Request timed out. Please try again.';
          } else if (
            error.message.includes('Failed to download') ||
            error.message.includes('HTTP') ||
            errorLike?.response?.status === 401 ||
            errorLike?.response?.status === 403
          ) {
            if (
              errorLike?.response?.status === 401 ||
              errorLike?.response?.status === 403
            ) {
              errorType = 'access-denied';
              errorDetails = "You don't have permission to preview this file.";
            } else {
              errorType = 'download-failed';
              errorDetails = error.message;
            }
          } else if (
            error.message.includes('fetch') ||
            error.message.includes('NetworkError') ||
            error.message.includes('Network')
          ) {
            errorType = 'network-error';
            errorDetails = 'Network error occurred while loading the file';
          } else {
            errorType = 'parse-error';
          }
        }

        setState({
          loading: false,
          error: errorType,
          data: {
            message: errorMessage,
            details: errorDetails,
          },
        });
      }
    };

    loadCSVContent();

    return () => {
      isCancelled = true;
    };
  }, [apiEndpoint, fileUrl, maxPreviewRows]);

  // Loading state
  if (state.loading) {
    return (
      <EmptyContent
        size="medium"
        icon={<IconTable />}
        title="Loading CSV Preview"
        description="Please wait while we load the file content..."
      />
    );
  }

  // Error states
  if (state.error) {
    const parseErrorMessage =
      state.data &&
      typeof state.data === 'object' &&
      'message' in state.data &&
      typeof (state.data as { message?: unknown }).message === 'string'
        ? (state.data as { message: string }).message
        : undefined;

    const errorMessages = {
      'access-denied':
        "You don't have permission to preview this file. Please contact your administrator if you believe this is an error.",
      'download-failed':
        'Failed to download the file. Please try again or download the file directly.',
      'parse-error':
        parseErrorMessage ||
        'The file appears to be corrupted or is not a valid CSV format.',
      'file-too-large':
        'The file is too large to preview. Please download it to view the full content.',
      'network-error':
        'Network error occurred while loading the file. Please check your connection.',
      'unsupported-format': 'This file format is not supported for preview.',
      'unknown-error':
        'An unexpected error occurred while loading the preview.',
    };

    const getErrorIcon = (errorType: string) => {
      switch (errorType) {
        case 'access-denied':
          return <IconAlertCircle />;
        default:
          return <IconAlertCircle />;
      }
    };

    return (
      <EmptyContent
        size="medium"
        state="error"
        icon={getErrorIcon(state.error)}
        title="Preview Not Available"
        description={errorMessages[state.error]}
      />
    );
  }

  const csvData = state.data as CSVPreviewData;

  if (!csvData || !csvData.headers || csvData.headers.length === 0) {
    return (
      <EmptyContent
        size="medium"
        icon={<IconFileText />}
        title="Empty CSV File"
        description="This CSV file appears to be empty or has no data to display."
      />
    );
  }

  // Create simple data structure for DataTable
  const tableData = csvData.rows.map((row, index) => {
    const rowData: Record<string, string> = { id: `row-${index}` };
    csvData.headers.forEach((header, headerIndex) => {
      rowData[`col_${headerIndex}`] = row[headerIndex] || '';
    });
    return rowData;
  });

  type CsvPreviewRow = Record<string, string>;

  // Create simple columns
  const tableColumns: ColumnDef<CsvPreviewRow>[] = csvData.headers.map(
    (header, index) => ({
      id: `col_${index}`,
      accessorKey: `col_${index}`,
      header: header || `Column ${index + 1}`,
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return (
          <Text size="2xsmall" title={value}>
            {value || ''}
          </Text>
        );
      },
      meta: {
        width: 'auto',
      },
    })
  );

  return (
    <DataTable
      // Warning if preview is limited
      toolbarLeftSlot={
        csvData.previewRows < csvData.totalRows && (
          <Callout
            size="small"
            color="warning"
            variant="surface"
            icon={<IconAlertTriangleFilled />}
          >
            This is a preview showing the first {csvData.previewRows} rows.
            Download the file to see all {csvData.totalRows} rows.
          </Callout>
        )
      }
      data={tableData}
      columns={tableColumns}
      enableSorting={true}
      enableFiltering={true}
      enablePagination={true}
      enableColumnVisibility={false}
      enableRowSelection={false}
      searchPlaceholder="Search CSV data..."
      emptyMessage="No data found"
      emptyIcon={<IconFileText />}
      pageSizeOptions={[10, 25, 50, 100]}
      getRowId={row => row.id}
      toolbarRightSlot={
        <>
          {/* File info */}
          <Flex justify="between" align="center" wrap="wrap" gap="2">
            <Text size="2" color="gray">
              Showing {csvData.previewRows} of {csvData.totalRows} rows •
              {csvData.previewRows < csvData.totalRows && ' (preview limited)'}
            </Text>
            <Text size="2" color="gray">
              {csvData.headers.length} columns
            </Text>
          </Flex>
        </>
      }
    />
  );
};

export default CSVPreviewRenderer;
